// Widget de Wompi en modo `tokenize`: la tarjeta se digita en el iframe de checkout.wompi.co y Kairos
// solo recibe `token` y `type`. Contrato verificado contra widget.js: la doc oficial solo muestra el form.

export type WompiConfig = {public_key: string; base_url: string}

/** Nunca PAN/CVC: esos se quedan dentro del iframe de Wompi. */
export type TokenizedSource = {
  token: string
  tipo: 'tarjeta' | 'nequi'
  acceptanceToken: string
  acceptPersonalAuth: string
  brand: string | null
  last4: string | null
  expMonth: string | null
  expYear: string | null
}

/** Su `message` es la clave i18n que la UI traduce. */
export class WompiWidgetError extends Error {}

export const WIDGET_SRC = 'https://checkout.wompi.co/widget.js'
const WIDGET_ORIGIN = 'https://checkout.wompi.co'
const CLOSE_EVENTS = ['escpressed', 'merchantreturnclicked']

// `POST /v1/payment_sources` solo acepta CARD y NEQUI, y `configAsTokenize` ignora `paymentMethods`:
// otro tipo se rechaza dentro de la app.
const TIPO_BY_WOMPI_TYPE: Record<string, 'tarjeta' | 'nequi'> = {CARD: 'tarjeta', NEQUI: 'nequi'}

type WidgetPaymentSource = {
  token?: string
  type?: string
  brand?: string | null
  last_four?: string | null
  exp_month?: string | null
  exp_year?: string | null
  public_data?: {brand?: string | null; last_four?: string | null; exp_month?: string | null; exp_year?: string | null}
}

type WidgetCheckout = {open: (callback: (result: {payment_source?: WidgetPaymentSource}) => void) => void}
type WidgetCheckoutCtor = new (config: {publicKey: string; widgetOperation: 'tokenize'}) => WidgetCheckout
type WidgetWindow = Window & {WidgetCheckout?: WidgetCheckoutCtor}

function loadWidgetScript(): Promise<WidgetCheckoutCtor> {
  const loaded = (window as WidgetWindow).WidgetCheckout
  if (loaded) return Promise.resolve(loaded)
  return new Promise((resolve, reject) => {
    const fail = () => reject(new WompiWidgetError('planFacturacion.autoDebito.form.errorWidgetLoad'))
    const settle = () => {
      const ctor = (window as WidgetWindow).WidgetCheckout
      if (ctor) resolve(ctor)
      else fail()
    }
    const pending = document.querySelector<HTMLScriptElement>(`script[src="${WIDGET_SRC}"]`)
    const script = pending ?? document.createElement('script')
    script.addEventListener('load', settle)
    script.addEventListener('error', fail)
    if (pending) return
    script.src = WIDGET_SRC
    script.async = true
    document.head.appendChild(script)
  })
}

async function acceptanceTokens(config: WompiConfig): Promise<{acceptanceToken: string; acceptPersonalAuth: string}> {
  // Endpoint público: tokens presignados de términos y de datos personales, sin dato de tarjeta.
  const resp = await fetch(`${config.base_url}/merchants/${config.public_key}`)
  if (!resp.ok) throw new WompiWidgetError('planFacturacion.autoDebito.form.errorAcceptance')
  const data = (await resp.json())?.data
  const acceptanceToken: string | undefined = data?.presigned_acceptance?.acceptance_token
  const acceptPersonalAuth: string | undefined =
    data?.presigned_personal_data_auth?.acceptance_token ?? data?.presigned_acceptance?.acceptance_token
  if (!acceptanceToken || !acceptPersonalAuth)
    throw new WompiWidgetError('planFacturacion.autoDebito.form.errorAcceptance')
  return {acceptanceToken, acceptPersonalAuth}
}

function runWidget(ctor: WidgetCheckoutCtor, publicKey: string): Promise<WidgetPaymentSource | null> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (value: WidgetPaymentSource | null) => {
      if (settled) return
      settled = true
      window.removeEventListener('message', onMessage)
      resolve(value)
    }
    // El iframe avisa por postMessage el cierre con Esc o «volver al comercio»; ahí el callback nunca llega.
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== WIDGET_ORIGIN) return
      if (CLOSE_EVENTS.includes(event.data?.event)) finish(null)
    }
    window.addEventListener('message', onMessage)
    new ctor({publicKey, widgetOperation: 'tokenize'}).open((result) => finish(result?.payment_source ?? null))
  })
}

/** `null` si el pagador cerró la ventana sin tokenizar. */
export async function tokenizeWithWidget(config: WompiConfig): Promise<TokenizedSource | null> {
  const ctor = await loadWidgetScript()
  const source = await runWidget(ctor, config.public_key)
  if (source === null) return null
  const tipo = TIPO_BY_WOMPI_TYPE[String(source.type)]
  if (!tipo) throw new WompiWidgetError('planFacturacion.autoDebito.form.errorUnsupported')
  if (!source.token) throw new WompiWidgetError('planFacturacion.autoDebito.form.errorTokenize')
  const {acceptanceToken, acceptPersonalAuth} = await acceptanceTokens(config)
  // widget.js solo garantiza `token` y `type`: marca y últimos dígitos pueden faltar.
  const publicData = source.public_data ?? {}
  return {
    token: source.token,
    tipo,
    acceptanceToken,
    acceptPersonalAuth,
    brand: source.brand ?? publicData.brand ?? null,
    last4: source.last_four ?? publicData.last_four ?? null,
    expMonth: source.exp_month ?? publicData.exp_month ?? null,
    expYear: source.exp_year ?? publicData.exp_year ?? null,
  }
}
